import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins } from "@fortawesome/free-solid-svg-icons";
import config from "./config";
import { meetingApi, meetingOperationId } from "./meetingApi";

export default function MeetingAnalysis({ meetingId, participants, onAccepted, disabled, renderInline }) {
  const key = `meetingAnalysis:${localStorage.getItem("userID")}:${meetingId}`;
  const [operation, setOperation] = useState(() => {
    try { const saved = JSON.parse(sessionStorage.getItem(key));
      return /^[a-f0-9]{32}$/.test(saved?.requestId) && Array.isArray(saved.participantUserIds)
        && saved.participantUserIds.length > 0 && saved.participantUserIds.every(id => Number.isInteger(id) && id > 0) ? saved : null;
    } catch { return null; }
  });
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [credits, setCredits] = useState(null); const [loadingCredits, setLoadingCredits] = useState(false);
  const [results, setResults] = useState([]); const [error, setError] = useState(""); const inFlight = useRef(false);
  const eligible = participants.filter(p => p.status === "Accepted" && p.userId);
  const token = localStorage.getItem("token") || "";
  useEffect(() => {
    if (!open || !token) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let active = true;
    setLoadingCredits(true); setCredits(null);
    fetch(`${config.API_URL.replace(/\/$/, "")}/api/credits/account`, {
      method: "GET", signal: controller.signal, cache: "no-store", headers: { Authorization: `Bearer ${token}` },
    }).then(async response => {
      const value = await response.json();
      if (!response.ok || !Number.isInteger(value.availableReports) || value.availableReports < 0) throw new Error();
      if (active) setCredits(value.availableReports);
    }).catch(() => { if (active) setCredits(null); })
      .finally(() => { clearTimeout(timeout); if (active) setLoadingCredits(false); });
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [open, token, key]);
  async function start() {
    if (inFlight.current) return;
    const next = operation || { requestId: meetingOperationId(), participantUserIds: eligible.map(p => p.userId) };
    inFlight.current = true; setBusy(true); setError("");
    try {
      sessionStorage.setItem(key, JSON.stringify(next)); setOperation(next);
      const response = await meetingApi(`/${meetingId}/analyses`, "POST", next);
      if (!Array.isArray(response.results) || response.results.length !== next.participantUserIds.length
        || new Set(response.results.map(r => r.targetUserId)).size !== next.participantUserIds.length
        || response.results.some(r => !next.participantUserIds.includes(r.targetUserId) || (!r.request?.requestId && !r.error)))
        throw new Error("The response could not be confirmed. Retry this meeting analysis.");
      setResults(response.results); onAccepted(response.results);
    } catch (reason) { setError(reason.name === "AbortError" ? "The response could not be confirmed. Retry this meeting analysis." : reason.message); }
    finally { inFlight.current = false; setBusy(false); }
  }
  const action = <button type="button" className="remoteAnalyzeButton" disabled={disabled || !eligible.length || busy} onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls={`meeting-analysis-confirmation-${meetingId}`}>Analyze full meeting</button>;
  const panel = open && <div className="meeting-analysis-confirmation" id={`meeting-analysis-confirmation-${meetingId}`} role="region" aria-label="Full meeting analysis">
      <p>Request a five-minute analysis for {operation?.participantUserIds.length || eligible.length} joined participants, using each person's most recently connected device.</p>
      <p>Each PDF is emailed to you and its participant. Existing team roles and device approval preferences apply.</p>
      <p>Pending invitations are not analyzed. Offline or unavailable participants can be retried; accepted requests keep running.</p>
      {error && <p role="alert">{error}</p>}
      {!!results.length && <ul aria-label="Meeting analysis results">{results.map(result => <li key={result.targetUserId}>
        <strong>{participants.find(p => p.userId === result.targetUserId)?.name || "Participant"}: </strong>{result.error || "Analysis requested. Follow progress below."}
      </li>)}</ul>}
      <div className="meeting-actions meeting-analysis-actions">
        {(!results.length || results.some(r => r.error) || error) && <>
          <p><strong>Your account pays one credit per completed participant report up to {operation?.participantUserIds.length || eligible.length} credits.</strong></p>
          <button type="button" className="meeting-start-button" disabled={busy || disabled} onClick={start}>{busy ? "Requesting analyses..." : operation ? "Retry meeting analysis" : "Start meeting analysis"}</button>
        </>}
        <p aria-live="polite">Available credits: {loadingCredits ? "Loading..." : credits ?? "Unavailable"}{!loadingCredits && credits !== null && <FontAwesomeIcon icon={faCoins} className="meeting-analysis-credit-icon" aria-hidden="true" />}</p>
        {operation && <button type="button" disabled={busy} onClick={() => { sessionStorage.removeItem(key); setOperation(null); setResults([]); setError(""); }}>New meeting analysis</button>}
      </div>
    </div>;
  return renderInline ? renderInline({ action, panel }) : <>{action}{panel}</>;
}
